import { useTranslation } from "react-i18next";
import Select from "react-select";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import type { TruckYardVehicle } from "../../../store/truckYardStore";
import {
  vehiclesForGroup,
  type ManualRegistrationErrors,
  type ManualRegistrationFormData,
} from "./manualRegistration";

interface SelectOption {
  value: string;
  label: string;
}

interface FieldsThemeClasses {
  text: string;
  textSecondary: string;
}

/**
 * Los campos de "Nuevo registro manual": tipo, patio, grupo → unidad, placa,
 * conductor y notas.
 *
 * Salieron de `NewAccessModal` (Accesos) cuando Inspecciones necesitó el mismo
 * formulario para el paso 1 de una pipa sin tag. Es un componente CONTROLADO y
 * sin `<form>` ni botones propios: cada pantalla decide qué hace al enviar
 * —Accesos crea un evento y abre la pluma, Inspecciones actualiza el evento de
 * entrada que ya existe— y lo único que comparten es qué se pregunta y cómo.
 *
 * Las tres variantes que hacen falta para Inspecciones son props, no ramas
 * adentro: el tipo fijo en "entrada", el patio fijo en el de la inspección y
 * el tag especial a la vista pero sin poder editarse.
 */
export default function ManualRegistrationFields({
  themeClasses,
  isDarkMode,
  form,
  errors,
  onChange,
  onClearError,
  yards,
  groups,
  vehicles,
  lockedYardId,
  yardLockedHint,
  typeLocked = false,
  readOnlyTag,
}: {
  themeClasses: FieldsThemeClasses;
  isDarkMode: boolean;
  form: ManualRegistrationFormData;
  errors: ManualRegistrationErrors;
  onChange: (patch: Partial<ManualRegistrationFormData>) => void;
  onClearError: (field: keyof ManualRegistrationFormData) => void;
  yards: readonly { id: string; name: string }[];
  groups: readonly { id: string; name: string; vehicle_ids?: string[] }[];
  vehicles: TruckYardVehicle[];
  lockedYardId?: string;
  /** Por qué está fijo el patio; por omisión, el texto del vigilante. */
  yardLockedHint?: string;
  /** El tipo no se elige (paso 1 de una pipa sin tag: siempre es la entrada). */
  typeLocked?: boolean;
  /** Tag especial que originó el registro: se muestra, no se edita. */
  readOnlyTag?: { label: string; value: string } | null;
}) {
  const { t } = useTranslation("truckYard");

  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const inputCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`;
  const selectStyles = getCustomSelectStyles(isDarkMode);

  // Vehicle picker: when a group is selected we narrow the options to that
  // group's vehicles (Group.vehicle_ids are resolved server-side from its
  // devices). With no group selected, every vehicle is available. When the
  // catalog has nothing to offer, the operator can still type a plate manually
  // in the field below.
  const groupSelectOptions: SelectOption[] = groups.map((g) => ({
    value: g.id,
    label: g.name,
  }));
  const vehicleSelectOptions: SelectOption[] = vehiclesForGroup(
    vehicles,
    groups,
    form.group_id,
  ).map((v) => ({
    value: v.id,
    label: v.plate ? `${v.name || v.id} · ${v.plate}` : v.name || v.id,
  }));
  const selectedVehicle = form.vehicle_id
    ? vehicles.find((v) => v.id === form.vehicle_id) || null
    : null;

  const errorText = (field: keyof ManualRegistrationFormData): string | null => {
    const code = errors[field];
    if (code === "yardRequired") return t("access.form.selectYardError");
    if (code === "unitRequired") return t("access.form.errors.unitRequired");
    return null;
  };
  const yardError = errorText("yard_id");
  const plateError = errorText("vehicle_plate");

  const typeButton = (type: "entry" | "exit") => {
    const active = form.type === type;
    const isEntry = type === "entry";
    const activeBox = isEntry
      ? "border-green-500 bg-green-500/10"
      : "border-red-500 bg-red-500/10";
    const activeBubble = isEntry ? "bg-green-500/20" : "bg-red-500/20";
    const activeText = isEntry ? "text-green-400" : "text-red-400";
    return (
      <button
        type="button"
        onClick={() => onChange({ type })}
        disabled={typeLocked}
        aria-pressed={active}
        className={`flex flex-col items-center gap-2 px-4 py-4 rounded-xl border-2 transition-all ${active ? activeBox : `border-transparent ${surface}`} ${
          typeLocked ? (active ? "cursor-not-allowed" : "opacity-50 cursor-not-allowed") : ""
        }`}
      >
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${active ? activeBubble : isDarkMode ? "bg-dark-surface" : "bg-gray-200"}`}
        >
          <i
            className={`${isEntry ? "ri-login-box-line" : "ri-logout-box-line"} text-lg ${active ? activeText : themeClasses.textSecondary}`}
          ></i>
        </div>
        <span
          className={`text-sm font-medium ${active ? activeText : themeClasses.textSecondary}`}
        >
          {isEntry ? t("access.form.entry") : t("access.form.exit")}
        </span>
      </button>
    );
  };

  return (
    <>
      {/* Entry type selector */}
      <div>
        <label className={labelCls}>
          {t("access.form.recordType")} <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {typeButton("entry")}
          {typeButton("exit")}
        </div>
        {typeLocked && (
          <span
            className={`text-[11px] ${themeClasses.textSecondary} mt-1 inline-flex items-center gap-1`}
          >
            <i className="ri-lock-line"></i>
            {t("access.form.typeLockedHint")}
          </span>
        )}
      </div>

      {/* Yard */}
      <div>
        <label className={labelCls}>
          {t("access.form.yard")} <span className="text-red-500">*</span>
        </label>
        <select
          value={form.yard_id}
          onChange={(e) => {
            onChange({ yard_id: e.target.value });
            if (errors.yard_id) onClearError("yard_id");
          }}
          disabled={!!lockedYardId}
          className={`${inputCls} ${yardError ? "!border-red-500/60" : ""} ${
            lockedYardId ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          <option value="">{t("access.form.selectYard")}</option>
          {yards.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </select>
        {lockedYardId && (
          <span
            className={`text-[11px] ${themeClasses.textSecondary} mt-1 inline-flex items-center gap-1`}
          >
            <i className="ri-lock-line"></i>
            {yardLockedHint ?? t("access.form.yardLockedHint")}
          </span>
        )}
        {yardError && <span className="text-red-400 text-xs mt-1">{yardError}</span>}
      </div>

      {/* El tag compartido con el que entró la unidad. Sólo lectura: es lo que
          leyó la pluma y lo que liga este registro con su evento de entrada;
          editarlo acá lo desligaría. */}
      {readOnlyTag && (
        <div>
          <label className={labelCls}>{readOnlyTag.label}</label>
          <input
            type="text"
            value={readOnlyTag.value}
            readOnly
            disabled
            className={`${inputCls} font-mono opacity-70 cursor-not-allowed`}
          />
        </div>
      )}

      {/* Transporter group — narrows the vehicle picker below */}
      <div>
        <label className={labelCls}>{t("access.form.group")}</label>
        <Select
          options={groupSelectOptions}
          value={groupSelectOptions.find((o) => o.value === form.group_id) || null}
          onChange={(o) =>
            // Changing the group clears the vehicle (and its manual plate)
            // so a selection from another group can't leak through.
            onChange({
              group_id: (o as SelectOption | null)?.value || "",
              vehicle_id: "",
              vehicle_plate: "",
            })
          }
          styles={selectStyles}
          placeholder={t("access.form.allGroups")}
          isClearable
          isSearchable
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          menuPosition="fixed"
        />
      </div>

      {/* Vehicle picker (scoped by group when one is selected). Sets only
          vehicle_id; the catalog plate is resolved at submit time so the
          picker and the manual plate field stay fully independent. */}
      <div>
        <label className={labelCls}>{t("access.form.vehicle")}</label>
        <p
          className={`text-[11px] ${themeClasses.textSecondary} mb-1.5 inline-flex items-start gap-1`}
        >
          <i className="ri-information-line mt-px"></i>
          <span>{t("access.form.vehicleManualHint")}</span>
        </p>
        <Select
          options={vehicleSelectOptions}
          value={vehicleSelectOptions.find((o) => o.value === form.vehicle_id) || null}
          onChange={(o) => {
            const vehicle_id = (o as SelectOption | null)?.value || "";
            // Drop any stale manual plate when picking from the catalog.
            onChange({ vehicle_id, vehicle_plate: "" });
            // Picking a catalog vehicle satisfies the unit requirement.
            if (vehicle_id && errors.vehicle_plate) onClearError("vehicle_plate");
          }}
          styles={selectStyles}
          placeholder={t("access.form.selectVehicle")}
          isClearable
          isSearchable
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          menuPosition="fixed"
          noOptionsMessage={() =>
            form.group_id
              ? t("access.form.noVehiclesInGroup")
              : t("access.form.noVehicles")
          }
        />
        {selectedVehicle?.plate && (
          <span
            className={`text-[11px] ${themeClasses.textSecondary} mt-1 inline-flex items-center gap-1`}
          >
            <i className="ri-roadster-line"></i>
            {t("access.form.plateLabel", { plate: selectedVehicle.plate })}
          </span>
        )}
      </div>

      {/* Vehicle plate — manual fallback, shown only when no catalog
          vehicle is selected (e.g. a one-off / unregistered unit). */}
      {!form.vehicle_id && (
        <div>
          <label className={labelCls}>
            {t("access.form.plate")}
            {form.type === "entry" && <span className="text-red-500"> *</span>}
          </label>
          <input
            type="text"
            value={form.vehicle_plate}
            onChange={(e) => {
              onChange({ vehicle_plate: e.target.value });
              if (errors.vehicle_plate) onClearError("vehicle_plate");
            }}
            className={`${inputCls} ${plateError ? "!border-red-500/60" : ""}`}
            placeholder={t("access.form.platePlaceholder")}
          />
          {plateError && (
            <span className="text-red-400 text-xs mt-1 block">{plateError}</span>
          )}
        </div>
      )}

      {/* QR Scanner — disabled for now (commented until we confirm removal).
          Vivía en NewAccessModal con su propio estado `showScanner`; si vuelve,
          el estado se queda en quien monta estos campos y acá sólo va el botón.
      <div>
        <label className={labelCls}>Escanear QR / código</label>
        {showScanner ? (
          <QRScanner isDarkMode={isDarkMode} themeClasses={themeClasses} onScan={handleQRScan} onClose={() => setShowScanner(false)} />
        ) : (
          <button type="button" onClick={() => setShowScanner(true)}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed transition-all ${isDarkMode ? "border-gray-600 hover:border-gray-500 hover:bg-dark-surface" : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"}`}>
            <i className={`ri-qr-scan-2-line text-xl ${themeClasses.textSecondary}`}></i>
            <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Escanear código QR</span>
          </button>
        )}
      </div>
      */}

      {/* Driver (full width; No. tarjeta field commented out for now) */}
      <div>
        <label className={labelCls}>{t("access.form.driver")}</label>
        <input
          type="text"
          value={form.driver_name}
          onChange={(e) => onChange({ driver_name: e.target.value })}
          className={inputCls}
          placeholder={t("access.form.driverPlaceholder")}
        />
      </div>
      {/* No. tarjeta — disabled for now (commented until we confirm removal)
      <div>
        <label className={labelCls}>No. tarjeta</label>
        <input type="text" value={form.card_number}
          onChange={(e) => onChange({ card_number: e.target.value })}
          className={`${inputCls} font-mono`} placeholder="RFID" />
      </div>
      */}

      {/* Notes */}
      <div>
        <label className={labelCls}>{t("access.form.notes")}</label>
        <textarea
          value={form.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          rows={2}
          className={inputCls}
          placeholder={t("access.form.notesPlaceholder")}
        />
      </div>
    </>
  );
}
